<script>
  /**
   * @type {{
   *   title: string,
   *   description?: string,
   *   url: string,
   *   emoji?: string,
   *   thumbnailUrl?: string,
   *   type: 'post' | 'external' | 'file',
   *   clicks?: number,
   *   date?: string,
   *   theme?: string,
   *   variant?: 'default' | 'corporate' | 'ocean' | 'brutal' | 'nature',
   *   accentColor?: string,
   *   size?: 'default' | 'featured',
   *   animation?: 'none' | 'pulse' | 'bounce' | 'wiggle',
   *   expiresAt?: string | null,
   * }}
   */
  let {
    title, description = '', url, emoji = '', thumbnailUrl = '',
    type = 'post', clicks = 0, date = '', theme = 'clean',
    variant = 'default', accentColor = '',
    size = 'default', animation = 'none', expiresAt = null,
  } = $props()

  function getTypeIcon(t) {
    if (t === 'external') return 'M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14'
    if (t === 'file') return 'M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z'
    return 'M9 5l7 7-7 7'
  }

  // Countdown label for expiresAt — renders a relative D-day badge
  // (D-3, D-1, 오늘 마감, 만료됨). Null when no expiry is set. Compares
  // at the day boundary (midnight-to-midnight) rather than exact
  // milliseconds so "6시간 남음 on today" renders as 오늘 마감, not D-1.
  function countdownLabel(iso) {
    if (!iso) return null
    const target = new Date(iso)
    if (Number.isNaN(target.getTime())) return null
    const dayIndex = (d) => Math.floor((d.getTime() - d.getTimezoneOffset() * 60000) / (1000 * 60 * 60 * 24))
    const days = dayIndex(target) - dayIndex(new Date())
    if (days < 0) return '만료됨'
    if (days === 0) return '오늘 마감'
    if (days <= 7) return `D-${days}`
    return `${days}일 남음`
  }
  const countdown = $derived(countdownLabel(expiresAt))
</script>

<a
  href={url}
  class="link-card"
  class:corporate-card={variant === 'corporate'}
  class:ocean-pill={variant === 'ocean'}
  class:brutal-card={variant === 'brutal'}
  class:nature-pill={variant === 'nature'}
  class:link-featured={size === 'featured'}
  class:link-animate-pulse={animation === 'pulse'}
  class:link-animate-bounce={animation === 'bounce'}
  class:link-animate-wiggle={animation === 'wiggle'}
  style={accentColor ? `background: ${accentColor}` : ''}
>
  {#if variant === 'corporate'}
    <div class="corporate-indicator"></div>
  {/if}
  {#if size === 'featured' && thumbnailUrl}
    <img src={thumbnailUrl} alt="{title} 미리보기" loading="lazy" class="link-featured-thumb" />
  {/if}
  <div class="link-left">
    {#if size !== 'featured' && thumbnailUrl}
      <img src={thumbnailUrl} alt="{title} 미리보기" loading="lazy" class="link-thumb" />
    {:else if emoji}
      <span class="link-emoji">{emoji}</span>
    {/if}
    <div class="link-text">
      <span class="link-title">{title}</span>
      {#if description}
        <span class="link-desc">{description}</span>
      {/if}
      {#if countdown}
        <span class="link-countdown" class:link-countdown-urgent={countdown === '오늘 마감' || countdown === '만료됨'}>
          ⏳ {countdown}
        </span>
      {/if}
    </div>
  </div>
  <div class="link-right">
    {#if clicks}
      <span class="link-clicks">{clicks}</span>
    {/if}
    <svg class="link-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width={variant === 'brutal' ? '2.5' : '2'}>
      <path d={getTypeIcon(type)} />
    </svg>
  </div>
</a>

<style>
  .link-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    background: var(--button-bg, var(--card-bg));
    border: 1px solid var(--button-border, var(--card-border));
    border-radius: var(--card-radius);
    box-shadow: var(--button-shadow, var(--card-shadow));
    text-decoration: none;
    transition: all 200ms cubic-bezier(0.4, 0, 0.2, 1);
    cursor: pointer;
    position: relative;
    overflow: hidden;
    backdrop-filter: var(--button-backdrop, none);
    -webkit-backdrop-filter: var(--button-backdrop, none);
  }

  .link-card:hover {
    background: var(--card-hover, var(--button-bg, var(--card-bg)));
    transform: translateY(-1px);
  }

  /* Corporate */
  .corporate-card {
    padding-left: 20px;
  }

  .corporate-indicator {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: var(--accent);
    border-radius: 0 2px 2px 0;
  }

  /* Ocean pill */
  .ocean-pill {
    border-radius: 9999px;
    padding: 14px 20px;
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
  }

  /* Neubrutalism */
  .brutal-card {
    border: 3px solid var(--text-primary) !important;
    box-shadow: 4px 4px 0px var(--text-primary);
    border-radius: 4px !important;
    font-weight: 700;
    transition: all 100ms ease !important;
  }

  .brutal-card:hover {
    box-shadow: 2px 2px 0px var(--text-primary) !important;
    transform: translate(2px, 2px) !important;
  }

  .brutal-card .link-title {
    font-weight: 800;
    font-size: 15px;
  }

  .brutal-card .link-desc {
    color: var(--text-secondary);
    font-weight: 600;
  }

  .brutal-card .link-clicks {
    font-weight: 800;
    color: var(--text-primary);
    background: rgba(255,255,255,0.5);
    padding: 2px 8px;
    border-radius: 2px;
    border: 2px solid var(--text-primary);
    font-size: 11px;
  }

  .brutal-card .link-arrow {
    color: var(--text-primary);
  }

  /* Nature pill */
  .nature-pill {
    border-radius: 24px !important;
    border: 1px solid var(--card-border) !important;
    padding: 14px 20px !important;
    transition: all 250ms cubic-bezier(0.4, 0, 0.2, 1) !important;
  }

  .nature-pill:hover {
    border-color: var(--accent) !important;
    transform: scale(1.01) !important;
    box-shadow: 0 2px 8px rgba(198, 124, 91, 0.12) !important;
  }

  .nature-pill .link-title {
    color: var(--button-text, var(--text-primary));
    font-weight: 600;
  }

  .nature-pill .link-desc {
    color: var(--button-subtext, var(--text-secondary));
  }

  .nature-pill .link-clicks {
    color: var(--button-subtext, var(--accent));
    font-weight: 600;
  }

  .nature-pill .link-arrow {
    color: var(--button-subtext, var(--text-secondary));
  }

  /* Shared inner elements */
  .link-left {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    min-width: 0;
  }

  .link-thumb {
    width: 44px;
    height: 44px;
    border-radius: 8px;
    object-fit: cover;
    flex-shrink: 0;
    background: color-mix(in srgb, var(--card-bg, #f1f5f9) 80%, var(--page-bg));
  }

  .link-emoji {
    font-size: 20px;
    flex-shrink: 0;
    width: 32px;
    text-align: center;
  }

  .link-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .link-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--button-text, var(--text-primary));
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .link-desc {
    font-size: 12px;
    color: var(--button-subtext, var(--text-secondary));
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .link-right {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    margin-left: 8px;
  }

  .link-clicks {
    font-size: 11px;
    color: var(--button-subtext, var(--text-tertiary));
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    background: color-mix(in srgb, var(--card-bg, #f1f5f9) 60%, var(--page-bg, #f1f5f9));
    padding: 2px 8px;
    border-radius: 9999px;
  }

  .link-arrow {
    width: 16px;
    height: 16px;
    color: var(--button-subtext, var(--text-tertiary));
    flex-shrink: 0;
  }

  /* ── Featured size ─────────────────────────────────────────────
     Linktree-style hero card — large thumbnail stacked above the
     title/description for announcements or priority destinations.
     The thumbnail fills a 16:9 frame at the top and the content row
     drops to a column layout for breathing room. */
  .link-featured {
    flex-direction: column;
    align-items: stretch;
    padding: 0;
    overflow: hidden;
  }
  .link-featured .link-left {
    padding: 16px;
    align-items: flex-start;
  }
  .link-featured .link-right {
    position: absolute;
    top: 12px;
    right: 12px;
  }
  .link-featured-thumb {
    width: 100%;
    aspect-ratio: 16 / 9;
    object-fit: cover;
    display: block;
  }
  .link-featured .link-title {
    font-size: 16px;
    font-weight: 700;
  }

  /* ── Schedule countdown ─────────────────────────────────────────
     Relative D-day badge rendered inline under the description.
     Urgent states (today, expired) switch to a warning red. */
  .link-countdown {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-top: 4px;
    padding: 2px 8px;
    border-radius: 9999px;
    background: color-mix(in srgb, var(--accent, #64748b) 12%, transparent);
    color: var(--accent, var(--text-secondary));
    font-size: 11px;
    font-weight: 600;
    width: fit-content;
  }
  .link-countdown-urgent {
    background: rgba(244, 63, 94, 0.14);
    color: #be123c;
  }

  /* ── Priority pin animations ────────────────────────────────────
     Subtle attention-getters for spotlighting a single link. Applied
     via animation: prop. Respects prefers-reduced-motion. */
  @keyframes link-pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.02); }
  }
  @keyframes link-bounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-4px); }
  }
  @keyframes link-wiggle {
    0%, 100% { transform: rotate(0deg); }
    25% { transform: rotate(-1deg); }
    75% { transform: rotate(1deg); }
  }
  .link-animate-pulse { animation: link-pulse 2.4s ease-in-out infinite; }
  .link-animate-bounce { animation: link-bounce 2s ease-in-out infinite; }
  .link-animate-wiggle { animation: link-wiggle 2.2s ease-in-out infinite; }
  @media (prefers-reduced-motion: reduce) {
    .link-animate-pulse,
    .link-animate-bounce,
    .link-animate-wiggle { animation: none; }
  }
</style>
